import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserAttribute,
  type CognitoUserSession,
  type ISignUpResult,
} from 'amazon-cognito-identity-js';

// --- Pool Configuration ---

const POOL_DATA = {
  UserPoolId: import.meta.env.VITE_COGNITO_USER_POOL_ID,
  ClientId: import.meta.env.VITE_COGNITO_CLIENT_ID,
};

let _pool: CognitoUserPool | null = null;

function getUserPool(): CognitoUserPool {
  if (!_pool) {
    if (!POOL_DATA.UserPoolId || !POOL_DATA.ClientId) {
      throw new Error(
        'Cognito User Pool configuration is missing. Check VITE_COGNITO_USER_POOL_ID and VITE_COGNITO_CLIENT_ID.',
      );
    }
    _pool = new CognitoUserPool(POOL_DATA);
  }
  return _pool;
}

function getCognitoUser(email: string): CognitoUser {
  return new CognitoUser({ Username: email, Pool: getUserPool() });
}

// --- Token Interfaces ---

export interface CognitoTokens {
  idToken: string;
  accessToken: string;
  refreshToken: string;
}

export interface CognitoAuthResult {
  tokens: CognitoTokens;
  userId: string;
  email: string;
}

function extractAuthResult(session: CognitoUserSession, email: string): CognitoAuthResult {
  const idToken = session.getIdToken();
  const payload = idToken.decodePayload();
  return {
    tokens: {
      idToken: idToken.getJwtToken(),
      accessToken: session.getAccessToken().getJwtToken(),
      refreshToken: session.getRefreshToken().getToken(),
    },
    userId: payload['sub'] as string,
    email: (payload['email'] as string) ?? email,
  };
}

// --- Sign Up ---

export function signUp(email: string, password: string): Promise<ISignUpResult> {
  return new Promise((resolve, reject) => {
    const attributeList = [new CognitoUserAttribute({ Name: 'email', Value: email })];

    getUserPool().signUp(email, password, attributeList, [], (err, result) => {
      if (err) return reject(err);
      if (!result) return reject(new Error('Sign up returned no result'));
      resolve(result);
    });
  });
}

// --- Confirm Sign Up ---

export function confirmSignUp(email: string, code: string): Promise<void> {
  return new Promise((resolve, reject) => {
    getCognitoUser(email).confirmRegistration(code, true, (err) => {
      if (err) return reject(err);
      resolve();
    });
  });
}

// --- Resend Confirmation Code ---

export function resendConfirmationCode(email: string): Promise<void> {
  return new Promise((resolve, reject) => {
    getCognitoUser(email).resendConfirmationCode((err) => {
      if (err) return reject(err);
      resolve();
    });
  });
}

// --- Sign In ---

export function signIn(email: string, password: string): Promise<CognitoAuthResult> {
  return new Promise((resolve, reject) => {
    const cognitoUser = getCognitoUser(email);
    const authDetails = new AuthenticationDetails({ Username: email, Password: password });

    cognitoUser.authenticateUser(authDetails, {
      onSuccess: (session) => resolve(extractAuthResult(session, email)),
      onFailure: (err) => reject(err),
    });
  });
}

// --- Get Current Session (restores from localStorage) ---

export function getCurrentSession(): Promise<CognitoAuthResult | null> {
  return new Promise((resolve) => {
    const cognitoUser = getUserPool().getCurrentUser();
    if (!cognitoUser) return resolve(null);

    cognitoUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session || !session.isValid()) return resolve(null);
      resolve(extractAuthResult(session, cognitoUser.getUsername()));
    });
  });
}

// --- Refresh Session ---

export function refreshSession(): Promise<CognitoAuthResult | null> {
  return new Promise((resolve) => {
    const cognitoUser = getUserPool().getCurrentUser();
    if (!cognitoUser) return resolve(null);

    cognitoUser.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session) return resolve(null);

      cognitoUser.refreshSession(session.getRefreshToken(), (refreshErr, newSession) => {
        if (refreshErr || !newSession) return resolve(null);
        resolve(extractAuthResult(newSession as CognitoUserSession, cognitoUser.getUsername()));
      });
    });
  });
}

// --- Sign Out ---

export function signOut(): void {
  getUserPool().getCurrentUser()?.signOut();
}
